/* Peter Augustus | 1 October 2026 | Blog article FAQ standard
   Adds existing shared typography hooks to legacy article HTML at render time.
   assets/pa-faq-standards.css is the single source: Montserrat Q14 / A12 / line20.
   No stored article copy, JSON-LD, links, toggle behaviour or layout is changed.
   Covers default/recipe templates, native FAQ accordions, static cards, inline
   HTML, and flat question/answer headings. Non-FAQ headings and terms accordions
   are excluded. Authors should use data-pa-faq-question / data-pa-faq-answer on
   text elements for new FAQs; automatic recognition supports older articles.
   Runs once at DOM ready and again after a theme-editor section reload. */
(() => {
  const questionHook = 'data-pa-faq-question';
  const answerHook = 'data-pa-faq-answer';
  const candidates = 'summary, h3, h4, strong, b, p, [class*="faq-question"]';
  const isQuestion = (el) => /\?\s*$/.test(el.textContent.trim());
  const markAnswer = (el) => {
    if (!el.hasAttribute(questionHook)) el.setAttribute(answerHook, '');
  };
  const markQuestion = (el) => {
    el.removeAttribute(answerHook);
    el.setAttribute(questionHook, '');
    if (el.tagName === 'SUMMARY') {
      Array.from(el.parentElement.children).forEach((child) => {
        if (child !== el && !child.matches('style, script')) markAnswer(child);
      });
      return;
    }
    let next = el.nextElementSibling;
    while (next && !next.matches('h1, h2, h3, h4, summary, style, script') &&
           !next.hasAttribute(questionHook) && !isQuestion(next)) {
      if (next.matches('p, ul, ol, blockquote, div')) markAnswer(next);
      next = next.nextElementSibling;
    }
  };
  const scan = (root) => {
    const elements = root.matches(candidates) ? [root] : [];
    elements.push(...root.querySelectorAll(candidates));
    elements.forEach((el) => {
      if (el.closest('[data-pa-faq-answer]')) return;
      if (el.tagName !== 'SUMMARY' && el.closest('details')) return;
      if (el.tagName === 'P' && !el.nextElementSibling?.matches('p')) return;
      if (el.matches('strong, b') && el.closest('p')) return;
      if (isQuestion(el)) markQuestion(el);
    });
  };
  const apply = (scope = document) => {
    scope.querySelectorAll('.article-content').forEach((content) => {
      // The article inventory uses native summary questions for FAQ toggles.
      // Question punctuation distinguishes them from "Terms and conditions".
      content.querySelectorAll('details > summary').forEach((el) => {
        if (isQuestion(el)) markQuestion(el);
      });
      content.querySelectorAll('[class*="faq"], [id*="faq"], [id*="frequently-asked"]').forEach(scan);
      content.querySelectorAll('h2, h3').forEach((heading) => {
        const title = heading.textContent.trim();
        const faqHeading = /\bFAQs?\b|frequently\s+asked|common\s+questions|^good\s+to\s+know$/i.test(title);
        const firstQuestion = /faq/i.test(heading.id) && isQuestion(heading);
        if (!faqHeading && !firstQuestion) return;
        if (firstQuestion) markQuestion(heading);
        const section = heading.closest('section');
        if (section && content.contains(section)) {
          scan(section);
          return;
        }
        // Static FAQ cards often have an enclosing div; flat imported articles
        // place the FAQ heading and h3/p pairs directly in their content wrapper.
        const parent = heading.parentElement;
        const siblingHeadings = Array.from(parent.children).filter((el) => el.matches('h1, h2'));
        if (parent !== content && siblingHeadings.length <= 1 && !firstQuestion) {
          scan(parent);
          return;
        }
        let next = heading.nextElementSibling;
        const level = Number(heading.tagName.slice(1));
        while (next && !(next.matches('h1, h2, h3') && Number(next.tagName.slice(1)) <= (firstQuestion ? 2 : level))) {
          scan(next);
          next = next.nextElementSibling;
        }
      });
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => apply(), { once: true });
  } else {
    apply();
  }
  document.addEventListener('shopify:section:load', (event) => apply(event.target));
})();
